
import { Link } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useActivation } from '@/contexts/ActivationContext';
import { useEffect, useState } from 'react';
import CartWidget from '@/components/adelantos/CartWidget';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

interface NavItem {
  label: string;
  href: string;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard' },
  { label: 'Cartola', href: '/cartola' },
  { label: 'Adelantos', href: '/adelantos/cuotas' },
  { label: 'Configuraciones', href: '/configuraciones' },
];

export default function Header() {
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { pendingCount, isLoading: activationLoading } = useActivation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (href: string) => {
    if (href === '/adelantos/cuotas') {
      return pathname.startsWith('/adelantos');
    }
    return pathname === href || pathname.startsWith(`${href}/`);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          <div className="flex items-center gap-4 md:gap-12">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="md:hidden relative inline-flex items-center justify-center h-10 w-10 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  aria-label="Abrir menú"
                >
                  <span className="material-symbols-outlined text-2xl">menu</span>
                  {!activationLoading && pendingCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 min-w-[16px] h-[16px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full inline-flex items-center justify-center leading-none">
                      {pendingCount}
                    </span>
                  )}
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 flex flex-col">
                <SheetHeader className="px-6 py-5 border-b border-slate-200 dark:border-slate-800 text-left">
                  <SheetTitle className="flex items-center gap-2 text-primary">
                    <span className="material-symbols-outlined text-2xl">account_balance_wallet</span>
                    <span className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">FLUX</span>
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex-1 px-3 py-4 space-y-1">
                  {navItems.map((item) => {
                    const active = isActive(item.href);
                    const showBadge =
                      item.href === '/configuraciones' && !activationLoading && pendingCount > 0;
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors ${
                          active
                            ? 'font-bold text-primary bg-primary/10'
                            : 'font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                      >
                        <span>{item.label}</span>
                        {showBadge && (
                          <span className="min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full inline-flex items-center justify-center leading-none">
                            {pendingCount}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </nav>
                <div className="border-t border-slate-200 dark:border-slate-800 px-3 py-3">
                  {user?.email && (
                    <div className="px-3 py-2 mb-2">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Conectado como</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{user.email}</p>
                    </div>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    <span className="material-symbols-outlined text-base">logout</span>
                    Cerrar sesión
                  </button>
                </div>
              </SheetContent>
            </Sheet>

            <Link to="/dashboard" className="flex items-center gap-2 text-primary">
              <span className="material-symbols-outlined text-3xl">account_balance_wallet</span>
              <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">FLUX</h1>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => {
                const showBadge =
                  item.href === '/configuraciones' && !activationLoading && pendingCount > 0;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`relative inline-flex items-center ${
                      isActive(item.href)
                        ? 'text-sm font-bold text-primary bg-primary/10 px-3 py-2 rounded-lg'
                        : 'text-sm font-medium text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition-colors'
                    }`}
                  >
                    {item.label}
                    {showBadge && (
                      <span className="ml-1.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full inline-flex items-center justify-center leading-none">
                        {pendingCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-6">
            {pathname.startsWith('/adelantos') && <CartWidget />}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="h-8 w-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-bold text-xs cursor-pointer hover:bg-primary/20 transition-colors"
                aria-label="User menu"
              >
                {user?.initials || 'JD'}
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1 z-50">
                  <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-700">
                    <p className="text-xs text-slate-500 dark:text-slate-400">Conectado como</p>
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
                  >
                    <span className="material-symbols-outlined text-base">logout</span>
                    Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showUserMenu && (
        <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
      )}
    </header>
  );
}
